// @ts-strict-ignore
import type { TEditableProps, Value } from '@udecode/plate';
import {
  MentionCombobox,
  offset,
  Plate,
  PlateProvider,
  size,
} from '@udecode/plate';
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Button } from '@/components/Button/Button';
import { IconButton as NeedlIconButton } from '@/components/IconButton/IconButton';
import { useDetectChannelId } from '@/features/Channels/hooks/useDetectChannelId';
import { useFetchChannel } from '@/features/Channels/hooks/useFetchChannel';
import { getNoteDocumentBodyText } from '@/features/Notes/hooks/useNoteDocumentBody';
import SendIcon from '@/icons/send.svg?react';

import { editorProps as defaultEditorProps, getPlugins } from '.';

const reducer =
  (submitEmpty: boolean) =>
  (_state: ActionState, action: ActionStateAction): ActionState => {
    if (submitEmpty) {
      return { canSave: true };
    }

    switch (action.type) {
      case 'CAN_SAVE':
        return { canSave: true };
      case 'CANNOT_SAVE':
        return { canSave: false };
    }
  };

export const Editor = ({
  editorProps = {},
  initialValue,
  onChange,
  onSave,
  ...rest
}: EditorType) => {
  const [focusState, setFocusState] = React.useState<'blurred' | 'focused'>(
    'blurred'
  );
  const [state, dispatch] = React.useReducer(
    reducer(rest.type === 'edit' ? rest.canSubmitEmpty : false),
    {
      canSave: !isDisabled(initialValue),
    }
  );
  const { channelId } = useDetectChannelId();
  const { channel } = useFetchChannel(channelId);

  const MENTIONABLES = React.useMemo(() => {
    return (
      channel?.members?.map((member) => ({
        key: member.user_id,
        text: member.username,
      })) ?? []
    );
  }, [channel]);

  const { canSave } = state;

  React.useEffect(() => {
    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          return;
        }
        if (canSave) {
          onSave();
        }
      }

      if (event.key === 'Escape' && rest.type === 'edit') {
        rest.onDiscard();
        return;
      }
    };

    if (focusState === 'focused') {
      window.addEventListener('keydown', keydownHandler);
    }
    return () => {
      window.removeEventListener('keydown', keydownHandler);
    };
  }, [canSave, focusState, onSave, rest]);

  const changeHandler = (value: Value) => {
    onChange?.(value);

    const disabled = isDisabled(value);

    if (canSave !== !disabled) {
      dispatch({ type: disabled ? 'CANNOT_SAVE' : 'CAN_SAVE' });
    }
  };

  const focusHandler = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    editorProps.onFocus?.(e);
    if (e.defaultPrevented) {
      return;
    }
    setFocusState('focused');
  };

  const blurHandler = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    editorProps.onBlur?.(e);
    if (e.defaultPrevented) {
      return;
    }
    setFocusState('blurred');
  };

  return (
    <div className='flex sm:block'>
      <div className='grow'>
        <PlateProvider
          initialValue={initialValue}
          plugins={getPlugins({ allowMentions: !channel?.publish })}
          onChange={changeHandler}
        >
          <Plate
            editableProps={{
              ...defaultEditorProps,
              ...editorProps,
              onFocus: focusHandler,
              onBlur: blurHandler,
              className: 'p-1 sm:px-3 sm:pt-2',
            }}
          >
            {!channel?.publish && (
              <MentionCombobox
                pluginKey='@'
                items={MENTIONABLES}
                floatingOptions={{
                  placement: 'top-start',
                  middleware: [
                    offset(0),
                    size({
                      apply({ availableHeight, elements }) {
                        Object.assign(elements.floating.style, {
                          maxHeight: `${Math.min(availableHeight - 10, 200)}px`,
                        });
                      },
                    }),
                  ],
                }}
              />
            )}
          </Plate>
        </PlateProvider>
      </div>
      <Footer canSave={canSave} onSave={onSave} type={rest.type} {...rest} />
    </div>
  );
};

const Footer = React.memo(({ canSave, onSave, ...rest }: FooterType) => {
  const isEditing = rest.type === 'edit';

  return (
    <FooterContainer>
      <div
        className={`ml-auto flex items-center gap-2 ${isEditing ? 'pb-1' : ''}`}
      >
        {isEditing ? (
          <>
            <Button
              label='Cancel'
              onClick={rest.onDiscard}
              size='medium'
              variant='action'
            />
            <Button
              label='Save'
              disabled={!canSave}
              onClick={onSave}
              size='medium'
              variant='primary'
            />
          </>
        ) : (
          <IconButton
            label='save comment'
            className={`text-emerald-900 ${!canSave ? 'opacity-50' : ''}`}
            disabled={!canSave}
            onClick={onSave}
            size='small'
          >
            <SendIcon />
          </IconButton>
        )}
      </div>
    </FooterContainer>
  );
});

const isDisabled = (value: Value) => {
  const editorValue = getNoteDocumentBodyText(JSON.stringify(value));
  return editorValue.length === 0;
};

const FooterContainer = styled.div`
  ${tw`flex items-center px-1`}
`;

const IconButton = styled(NeedlIconButton)`
  [data-icon-container] {
    ${tw`text-emerald-900`}
  }
`;

type Common = {
  /**
   * props to pass to the editable component
   */
  editorProps?: TEditableProps;
  /**
   * callabck to be called when the value of the editor changes
   */
  onChange?: (value: Value) => void;
  /**
   * callback to be called when the user clicks the save button.
   *
   * _**Callback should be memoized with `useCallback`.**_
   */
  onSave: () => void;
};

export type EditorType = Common &
  (
    | {
        type: 'create';
        /**
         * initial value of the editor
         */
        initialValue?: Value;
      }
    | {
        type: 'edit';
        /**
         * initial value of the editor
         */
        initialValue: Value;
        /**
         * callback to be called when the user clicks the dismiss button to discard an edit.
         *
         * _**Callback should be memoized with `useCallback`.**_
         */
        onDiscard: () => void;
        /**
         * allow the editor to be submitted with an empty value
         * which would be the case when the user deletes all the content
         * thus deleting the comment
         */
        canSubmitEmpty?: boolean;
      }
  );

type FooterType = {
  canSave: boolean;
  onSave: () => void;
} & ({ type: 'create' } | { type: 'edit'; onDiscard: () => void });

type ActionState = { canSave: boolean };

type ActionStateAction = { type: 'CAN_SAVE' } | { type: 'CANNOT_SAVE' };
